import React, { useState } from "react";
import {
  ArrowRight,
  MessageCircle,
  MoreVertical,
  Search,
  UserPlus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChat } from "@/context/ChatProvider";

interface Chat {
  id: string;
  name: string;
  message: string;
  unreadCount: number;
  avatar?: string;
  status: "online" | "offline" | "away";
  lastMessageTime: string;
  isGroup?: boolean;
}

interface ChatListItemProps {
  name: string;
  message: string;
  unreadCount?: number;
  isActive?: boolean;
  avatar?: string;
  status: "online" | "offline" | "away";
  lastMessageTime: string;
  isGroup?: boolean;
  onClick?: () => void;
}

const getStatusColor = (status: ChatListItemProps["status"]) => {
  switch (status) {
    case "online":
      return "bg-green-500";
    case "away":
      return "bg-yellow-500";
    case "offline":
      return "bg-gray-500";
  }
};

export default function ChatSidebar() {
  const { selectChat } = useChat();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<
    "all" | "unread" | "groups"
  >("all");

  const chats: Chat[] = [
    {
      id: "1",
      name: "John Doe",
      message: "Oi, como você está?",
      unreadCount: 2,
      avatar: "https://i.pravatar.cc/150?img=1",
      status: "online",
      lastMessageTime: "10:00",
    },
    {
      id: "2",
      name: "Desenvolvedor Hub",
      message: "Projeto atualizado.",
      unreadCount: 0,
      avatar: "https://i.pravatar.cc/150?img=2",
      status: "offline",
      lastMessageTime: "09:30",
      isGroup: true,
    },
    {
      id: "3",
      name: "Jane",
      message: "Vamos ao evento?",
      unreadCount: 1,
      avatar: "https://i.pravatar.cc/150?img=3",
      status: "away",
      lastMessageTime: "09:15",
    },
    {
      id: "4",
      name: "Raquel",
      message: "Quem eh jane ??",
      unreadCount: 1,
      avatar: "https://i.pravatar.cc/150?img=4",
      status: "online",
      lastMessageTime: "09:00",
    },
    {
      id: "5",
      name: "Grupo Hackathon",
      message: "Reunião às 15h.",
      unreadCount: 0,
      avatar: "https://i.pravatar.cc/150?img=5",
      status: "offline",
      lastMessageTime: "08:45",
      isGroup: true,
    },
  ];

  const filteredChats = chats.filter((chat) => {
    if (searchQuery) {
      return (
        chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chat.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    switch (selectedFilter) {
      case "unread":
        return chat.unreadCount > 0;
      case "groups":
        return chat.isGroup;
      default:
        return true;
    }
  });

  const handleChatClick = (chat: Chat) => {
    selectChat({
      id: chat.id,
      name: chat.name,
      avatar: chat.avatar,
      status: chat.status,
      isGroup: chat.isGroup || false,
    });
  };

  const handleOpenChatPage = () => {
    navigate("/chat");
  };

  return (
    <aside className="h-screen relative w-92 flex flex-col border-l border-gray-800 bg-[#1A1D23] shadow-xl transition-all duration-300">
      <header className="flex flex-col border-b border-gray-800">
        <div className="flex items-center justify-between px-4 py-4">
          <h3 className="text-lg text-white font-semibold">Chats</h3>
          <div className="flex gap-2">
            <button
              onClick={handleOpenChatPage}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowRight size={20} />
            </button>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
              <UserPlus size={20} />
            </button>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
              <MoreVertical size={20} />
            </button>
          </div>
        </div>

        <div className="px-4 pb-4">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Pesquisar chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#2A2F3B] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 px-4 pb-4">
          <button
            onClick={() => setSelectedFilter("all")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              selectedFilter === "all"
                ? "bg-red-600 text-white"
                : "bg-[#2A2F3B] text-gray-400 hover:bg-[#2A2F3B]/80"
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setSelectedFilter("unread")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              selectedFilter === "unread"
                ? "bg-red-600 text-white"
                : "bg-[#2A2F3B] text-gray-400 hover:bg-[#2A2F3B]/80"
            }`}
          >
            Não lidos
          </button>
          <button
            onClick={() => setSelectedFilter("groups")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              selectedFilter === "groups"
                ? "bg-red-600 text-white"
                : "bg-[#2A2F3B] text-gray-400 hover:bg-[#2A2F3B]/80"
            }`}
          >
            Grupos
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto scrollbar-custom">
        {filteredChats.map((chat) => (
          <ChatListItem
            key={chat.id}
            {...chat}
            onClick={() => handleChatClick(chat)}
          />
        ))}
      </div>

      <footer className="p-4 border-t border-gray-800">
        <button className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium transition-colors">
          <MessageCircle size={20} />
          Novo Chat
        </button>
      </footer>
    </aside>
  );
}

const ChatListItem: React.FC<ChatListItemProps> = ({
  name,
  message,
  unreadCount = 0,
  isActive,
  avatar,
  status,
  lastMessageTime,
  isGroup,
  onClick,
}) => {
  return (
    <div
      onClick={onClick}
      className={`flex items-start p-4 cursor-pointer hover:bg-[#2A2F3B] transition ${
        isActive ? "bg-[#2A2F3B]" : ""
      }`}
    >
      <div className="relative">
        <div className="w-12 h-12 rounded-full overflow-hidden">
          {avatar ? (
            <img
              src={avatar}
              alt={name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-700 flex items-center justify-center text-gray-400 text-lg font-semibold">
              {name[0]}
            </div>
          )}
        </div>
        {!isGroup && (
          <div
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[#1A1D23] ${getStatusColor(status)}`}
          />
        )}
      </div>

      <div className="ml-4 flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-white truncate">{name}</h4>
          <span className="text-xs text-gray-500 ml-2">{lastMessageTime}</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-400 truncate">{message}</p>
          {unreadCount > 0 && (
            <span className="ml-2 bg-red-600 text-xs text-white font-semibold px-2 py-1 rounded-full min-w-[20px] text-center">
              {unreadCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};